<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { IconPeople } from '@/components/design/icons';
import type { StaticColumnType } from '@/types/contractManagement/viewTypes';
import { addContractualPartyToContractFile, removeContractualPartyFromContractFile } from '@/api';
import type { ContractualParty } from '@/services/contractManagement/contractualPartyRow';
import { contractualPartyRoute } from '@/router/links';
import SelectEntityModal from './SelectEntityModal.vue';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import { gaiaNeutral } from '@/components/design/themes/tokens';
import { logger } from '@/utils/logger';
import { ACCESS_PERMISSION_WRITE, deniedReason } from '@/utils/accessPermission';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * Edit the contractual parties in one view cell — link more to the contract
 * file, or unlink one. The Contractual Parties column is the only static
 * column whose entities are edited in place.
 */

const router = useRouter();

interface EntityWithFile {
  entity: ContractualParty;
  contractFileUid: string;
  contractFileName?: string;
}

interface Props {
  show: boolean;
  staticColumnType: StaticColumnType | null;
  entities: ContractualParty[];
  contractFileUid: string | null;
  // For aggregated view (entities from multiple contract files)
  entitiesWithFiles?: EntityWithFile[];
}

interface Emits {
  (e: 'update:show', value: boolean): void;
  (e: 'removed'): void;
  (e: 'added'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const message = useGaiaToast();

const showModal = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

const removingUids = ref<Set<string>>(new Set());
const isRemoving = computed(() => removingUids.value.size > 0);
const showSelectEntityModal = ref(false);

// Check if we're using aggregated view (entities from multiple files)
const isAggregatedView = computed(() => {
  return props.entitiesWithFiles !== undefined && props.entitiesWithFiles.length > 0;
});

// Local reactive list of entities that can be modified immediately
const localEntities = ref<EntityWithFile[]>([]);

// Initialize local entities from props
function initializeLocalEntities(): void {
  if (isAggregatedView.value) {
    localEntities.value =
      props.entitiesWithFiles?.map(item => ({
        entity: item.entity,
        contractFileUid: item.contractFileUid,
        contractFileName: item.contractFileName,
      })) ?? [];
  } else {
    localEntities.value = props.entities.map(entity => ({
      entity,
      contractFileUid: props.contractFileUid ?? '',
      contractFileName: undefined,
    }));
  }
}

// Watch for modal opening and prop changes to ensure proper initialization
watch(
  () => [
    props.show,
    props.staticColumnType,
    props.entities,
    props.entitiesWithFiles,
    props.contractFileUid,
  ],
  () => {
    // Initialize when modal is open and has required props
    if (props.show && props.staticColumnType) {
      // Use nextTick to ensure Vue has processed all prop updates
      void nextTick(() => {
        initializeLocalEntities();
      });
    }
  },
  { immediate: true, deep: true }
);

// Reset state when modal closes
watch(showModal, newValue => {
  if (!newValue) {
    removingUids.value.clear();
  }
});

// Get all assigned entities from local list
const allAssignedEntities = computed(() => localEntities.value);

// Handle unassign entity
async function handleUnassign(entityUid: string, contractFileUid?: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
  const fileUid = contractFileUid || props.contractFileUid;

  if (!fileUid) {
    message.error('Contract file UID is required');
    return;
  }

  removingUids.value.add(entityUid);

  try {
    await removeContractualPartyFromContractFile({
      query: { contractual_party_uid: entityUid, contract_file_uid: fileUid },
      throwOnError: true,
    });
    message.success('Contractual party unassigned successfully');

    // Remove the entity from local list immediately
    localEntities.value = localEntities.value.filter(item => item.entity.uid !== entityUid);

    emit('removed');
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to unassign contractual party');
  } finally {
    removingUids.value.delete(entityUid);
  }
}

// Handle add entity button click
function handleAddEntity(): void {
  if (!props.contractFileUid) {
    message.error('Contract file UID is required');
    return;
  }
  showSelectEntityModal.value = true;
}

// Handle entity assigned from SelectEntityModal
async function handleEntityAssigned(entityUid: string): Promise<void> {
  if (!props.contractFileUid) {
    message.error('Contract file UID is missing');
    return;
  }

  try {
    await addContractualPartyToContractFile({
      query: { contractual_party_uid: entityUid, contract_file_uid: props.contractFileUid },
      throwOnError: true,
    });
    message.success('Contractual party assigned successfully');

    // The parent refetches and updates props.entities, which the watch picks up.
    emit('added');
    showSelectEntityModal.value = false;
  } catch (error) {
    logger.error('Error assigning contractual party:', error);
    message.error(error instanceof Error ? error.message : 'Failed to assign contractual party');
  }
}

// Handle navigation to entity detail view
function handleEntityClick(entity: ContractualParty, event: MouseEvent): void {
  // Prevent event from bubbling (the button has @click.stop, but this is extra safety)
  event.stopPropagation();

  // Close the modal first
  showModal.value = false;

  // Use nextTick to ensure modal closes before navigation
  void nextTick(() => {
    router.push(contractualPartyRoute(entity.uid)).catch(error => {
      logger.error('Navigation error:', error);
      message.error('Failed to navigate to the contractual party');
    });
  });
}

// --- Chrome ---
/**
 * Inline custom properties rather than `v-bind()` — the modal root is a
 * Teleport, so Vue has no root element in place to write `v-bind()` vars onto,
 * and every colour bound that way silently resolved to nothing.
 */
const styleVars = {
  '--edit-col-row-border': gaiaNeutral.borderCard,
  '--edit-col-row-border-hover': gaiaNeutral.borderHover,
  '--edit-col-surface': gaiaNeutral.surface,
};
const glyphColor = gaiaNeutral.textSubtle;
</script>

<template>
  <gaia-modal
    v-model:show="showModal"
    width="440px"
    :closable="false"
    content-style="padding: 20px"
    :style="styleVars"
  >
    <n-flex align="flex-start" :size="12" :wrap="false">
      <gaia-text size="subtitle" weight="semibold" tone="ink"> Edit contractual parties </gaia-text>
      <gaia-icon-button
        :dimension="26"
        quaternary
        title="Close"
        :style="{ marginLeft: 'auto' }"
        @click="showModal = false"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
        >
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      </gaia-icon-button>
    </n-flex>

    <gaia-banner
      v-if="!isAggregatedView && !contractFileUid"
      plain
      type="error"
      :style="{ marginTop: '14px' }"
    >
      Contract file UID is required to edit static columns.
    </gaia-banner>

    <template v-else>
      <gaia-text
        size="control"
        tone="subtle"
        :style="{ display: 'block', marginTop: '6px', lineHeight: 1.5 }"
      >
        Remove a party to unassign it from this contract file.
      </gaia-text>

      <gaia-add-button
        subject="contractual party"
        variant="primary"
        size="md"
        block
        :style="{ marginTop: '16px' }"
        @click="handleAddEntity"
      />

      <gaia-empty-state
        v-if="allAssignedEntities.length === 0"
        dashed
        title="No contractual parties assigned"
        :style="{ marginTop: '14px' }"
      >
        Add one to link it to this contract file.
      </gaia-empty-state>

      <div v-else class="entity-list">
        <div
          v-for="item in allAssignedEntities"
          :key="`${item.contractFileUid}-${item.entity.uid}`"
          class="entity-row"
        >
          <button type="button" class="entity-main" @click="handleEntityClick(item.entity, $event)">
            <n-icon :component="IconPeople" :size="15" :color="glyphColor" />
            <gaia-text truncate size="body" tone="ink">
              {{ item.entity.name || 'Unnamed' }}
            </gaia-text>
            <gaia-text
              v-if="item.contractFileName && isAggregatedView"
              truncate
              size="meta"
              tone="faint"
            >
              {{ item.contractFileName }}
            </gaia-text>
          </button>
          <gaia-delete-button
            intent="remove"
            :subject="item.entity.name || 'party'"
            :loading="removingUids.has(item.entity.uid)"
            :disabled="
              isRemoving || deniedReason(item.entity.permissions, ACCESS_PERMISSION_WRITE) !== null
            "
            :title="deniedReason(item.entity.permissions, ACCESS_PERMISSION_WRITE) ?? undefined"
            @click.stop="handleUnassign(item.entity.uid, item.contractFileUid)"
          />
        </div>
      </div>
    </template>

    <n-flex :size="8" justify="end" :style="{ marginTop: '18px' }">
      <gaia-button size="small" @click="showModal = false">Close</gaia-button>
    </n-flex>
  </gaia-modal>

  <!-- Select Entity Modal -->
  <SelectEntityModal
    v-if="showSelectEntityModal && contractFileUid"
    v-model:show="showSelectEntityModal"
    entity-type="contractual-party"
    :contract-file-uid="contractFileUid"
    @assigned="handleEntityAssigned"
  />
</template>

<style scoped>
/* The assigned rows. Each is a flat bordered row rather than a card so a long
   list reads as one list instead of a stack of tiles. */
.entity-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 14px;
  max-height: 46vh;
  overflow-y: auto;
}

.entity-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px 7px 11px;
  border: 1px solid var(--edit-col-row-border);
  border-radius: 9px;
  background: var(--edit-col-surface);
}

.entity-row:hover {
  border-color: var(--edit-col-row-border-hover);
}

/* The name opens the record, so it is the button — not the whole row, which
   also carries the unassign control. */
.entity-main {
  display: flex;
  align-items: center;
  gap: 9px;
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
</style>
